import { useState, useEffect } from 'react';
import { 
  Users, CheckCircle, Clock, XCircle, Search, Filter,
  Send, RefreshCw, Key, Eye, ChevronDown, AlertCircle,
  Loader2, Crown, Shield, Mail
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    approved: 'bg-green-500/20 text-green-400 border-green-500/30',
    waitlist: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    pending: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
  };

  return (
    <span className={`px-3 py-1 text-xs font-bold uppercase border ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

// Application Status Modal
const ApplicationStatusModal = ({ application, onClose, onUpdate }) => {
  const [selectedStatus, setSelectedStatus] = useState(application?.status || 'pending');
  const [notes, setNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpdate = async () => {
    setIsUpdating(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/auth/admin/update-application-status`, {
        application_id: application._id || application.id,
        status: selectedStatus,
        notes: notes
      });

      setResult({
        success: true,
        ...response.data
      });

      setTimeout(() => {
        onUpdate(response.data);
        onClose();
      }, 2000);
    } catch (err) {
      setResult({
        success: false,
        error: err.response?.data?.detail || 'Failed to update status'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0D0D0D] border border-white/10 max-w-lg w-full">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">Update Application Status</h2>
          <p className="text-white/50 text-sm mt-1">{application.email}</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Applicant Info */}
          <div className="bg-white/5 p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-white/50">Company</span>
              <span className="text-white">{application.company_name || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">MC Number</span>
              <span className="text-white">{application.mc_number || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Current Status</span>
              <StatusBadge status={application.status || 'pending'} />
            </div>
          </div>

          {/* Status Selection */}
          <div className="space-y-3">
            <label className="block text-white/70 text-sm">Select New Status</label>
            <div className="grid grid-cols-3 gap-3">
              {['approved', 'waitlist', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`py-3 px-4 border text-center transition-all ${
                    selectedStatus === status
                      ? status === 'approved'
                        ? 'bg-green-500/20 border-green-500 text-green-400'
                        : status === 'waitlist'
                          ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                          : 'bg-red-500/20 border-red-500 text-red-400'
                      : 'border-white/10 text-white/50 hover:border-white/30'
                  }`}
                >
                  {status === 'approved' && <CheckCircle className="w-5 h-5 mx-auto mb-1" />}
                  {status === 'waitlist' && <Clock className="w-5 h-5 mx-auto mb-1" />}
                  {status === 'rejected' && <XCircle className="w-5 h-5 mx-auto mb-1" />}
                  <span className="text-sm font-bold uppercase">{status}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="block text-white/70 text-sm">Internal Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-3 bg-[#121212] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none resize-none"
              rows={3}
              placeholder="Add notes about this decision..."
            />
          </div>

          {/* Info about what happens */}
          <div className={`p-4 border ${
            selectedStatus === 'approved'
              ? 'bg-green-500/10 border-green-500/30'
              : selectedStatus === 'waitlist'
                ? 'bg-yellow-500/10 border-yellow-500/30'
                : 'bg-red-500/10 border-red-500/30'
          }`}>
            <p className={`text-sm ${
              selectedStatus === 'approved'
                ? 'text-green-400'
                : selectedStatus === 'waitlist'
                  ? 'text-yellow-400'
                  : 'text-red-400'
            }`}>
              {selectedStatus === 'approved' && (
                <>
                  <strong>This will:</strong>
                  <br />• Generate a unique Access Code (QB-XXXX-XXXX)
                  <br />• Create user account in the Hive
                  <br />• Send Welcome Email with login instructions
                </>
              )}
              {selectedStatus === 'waitlist' && (
                <>
                  <strong>This will:</strong>
                  <br />• Send Waitlist notification email
                  <br />• Keep application for future consideration
                  <br />• No access code will be generated
                </>
              )}
              {selectedStatus === 'rejected' && (
                <>
                  <strong>This will:</strong>
                  <br />• Send Rejection notification email
                  <br />• No access code will be generated
                  <br />• Applicant can reapply in the future
                </>
              )}
            </p>
          </div>

          {/* Result Message */}
          {result && (
            <div className={`p-4 ${result.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              {result.success ? (
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-green-400 font-medium">Status Updated Successfully!</p>
                    {result.access_code && (
                      <p className="text-green-400/80 text-sm mt-1">
                        Access Code: <span className="font-mono font-bold">{result.access_code}</span>
                      </p>
                    )}
                    <p className="text-green-400/60 text-sm mt-1">Email sent to {result.email}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  <span>{result.error}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-white/20 text-white hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="px-6 py-2 bg-[#D4AF37] text-black font-bold hover:bg-[#F5D47B] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Update & Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// User Actions Modal
const UserActionsModal = ({ user, onClose, onAction }) => {
  const [action, setAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRegenerateCode = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/admin/regenerate-access-code`, {
        user_id: user.id || user._id
      });
      setResult({
        success: true,
        message: 'Access code regenerated',
        access_code: response.data.access_code
      });
      onAction?.();
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Failed to regenerate' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendLink = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/admin/resend-magic-link`, {
        email: user.email
      });
      setResult({ success: true, message: 'Magic link sent to ' + user.email });
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Failed to send' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendReminder = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/send-onboarding-reminder?email=${user.email}`);
      setResult({ success: true, message: 'Reminder sent to ' + user.email });
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Failed to send' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0D0D0D] border border-white/10 max-w-md w-full">
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">User Actions</h2>
          <p className="text-white/50 text-sm mt-1">{user.email}</p>
        </div>

        <div className="p-6 space-y-4">
          {/* User Info */}
          <div className="bg-white/5 p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-white/50">Access Code</span>
              <span className="text-[#D4AF37] font-mono">{user.access_code || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Status</span>
              <StatusBadge status={user.application_status || 'pending'} />
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Onboarding</span>
              <span className={user.documents_completed ? 'text-green-400' : 'text-yellow-400'}>
                {user.documents_completed ? 'Complete' : 'Incomplete'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleRegenerateCode}
              disabled={isLoading}
              className="w-full p-4 border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex items-center gap-3"
            >
              <Key className="w-5 h-5 text-[#D4AF37]" />
              <div className="text-left">
                <p className="text-white font-medium">Regenerate Access Code</p>
                <p className="text-white/50 text-sm">Generate a new code and send to user</p>
              </div>
            </button>

            <button
              onClick={handleResendLink}
              disabled={isLoading}
              className="w-full p-4 border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex items-center gap-3"
            >
              <Send className="w-5 h-5 text-[#D4AF37]" />
              <div className="text-left">
                <p className="text-white font-medium">Send Magic Login Link</p>
                <p className="text-white/50 text-sm">Send a secure login link to user's email</p>
              </div>
            </button>

            {!user.documents_completed && (
              <button
                onClick={handleSendReminder}
                disabled={isLoading}
                className="w-full p-4 border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex items-center gap-3"
              >
                <Mail className="w-5 h-5 text-[#D4AF37]" />
                <div className="text-left">
                  <p className="text-white font-medium">Send Onboarding Reminder</p>
                  <p className="text-white/50 text-sm">Remind user to complete documents</p>
                </div>
              </button>
            )}
          </div>

          {/* Result */}
          {result && (
            <div className={`p-4 ${result.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              {result.success ? (
                <div>
                  <p className="text-green-400 font-medium flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {result.message}
                  </p>
                  {result.access_code && (
                    <p className="text-green-400/80 text-sm mt-2">
                      New Code: <span className="font-mono font-bold">{result.access_code}</span>
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-red-400 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {result.error}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-white/20 text-white hover:bg-white/10 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Login Activity Modal
const LoginActivityModal = ({ user, onClose }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const response = await axios.get(`${API}/auth/admin/login-activity/${user.id || user._id}`);
        setActivities(response.data);
      } catch (err) {
        console.error('Failed to fetch activity:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [user]);

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0D0D0D] border border-white/10 max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">Login Activity</h2>
          <p className="text-white/50 text-sm mt-1">{user.email}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
            </div>
          ) : activities.length === 0 ? (
            <p className="text-white/50 text-center py-8">No login activity recorded</p>
          ) : (
            <div className="space-y-2">
              {activities.map((activity, index) => (
                <div key={index} className="p-4 bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${
                      activity.status === 'success' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {activity.action?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span className="text-white/40 text-xs">
                      {new Date(activity.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-white/40">IP: </span>
                      <span className="text-white/70 font-mono">{activity.ip_address}</span>
                    </div>
                    <div>
                      <span className="text-white/40">Status: </span>
                      <span className={activity.status === 'success' ? 'text-green-400' : 'text-red-400'}>
                        {activity.status}
                      </span>
                    </div>
                    {activity.reason && (
                      <div className="col-span-2">
                        <span className="text-white/40">Reason: </span>
                        <span className="text-white/70">{activity.reason}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-white/20 text-white hover:bg-white/10 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Admin Application Management Component
const AdminApplicationManagement = () => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingOnboarding, setPendingOnboarding] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('applications');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActivityFor, setShowActivityFor] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch applications
      const appRes = await axios.get(`${API}/admin/applications`).catch(() => ({ data: [] }));
      setApplications(appRes.data || []);

      // Fetch Hive users
      const userRes = await axios.get(`${API}/auth/hive/admin/users`).catch(() => ({ data: [] }));
      setUsers(userRes.data || []);

      // Fetch pending onboarding
      const pendingRes = await axios.get(`${API}/auth/admin/pending-onboarding`).catch(() => ({ data: [] }));
      setPendingOnboarding(pendingRes.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         app.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6" data-testid="admin-application-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Crown className="w-6 h-6 text-[#D4AF37]" />
            Application & Access Management
          </h2>
          <p className="text-white/50">Manage applications, access codes, and user authentication</p>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Total Applications</p>
              <p className="text-2xl font-bold text-white">{applications.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Approved</p>
              <p className="text-2xl font-bold text-white">
                {applications.filter(a => a.status === 'approved').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Pending Onboarding</p>
              <p className="text-2xl font-bold text-white">{pendingOnboarding.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#121212] border border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
              <Key className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-white/50 text-sm">Active Users</p>
              <p className="text-2xl font-bold text-white">{users.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 overflow-x-auto">
        {[
          { id: 'applications', label: 'Applications', icon: Users },
          { id: 'users', label: 'Hive Users', icon: Key },
          { id: 'pending', label: 'Pending Onboarding', icon: Clock }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 flex items-center gap-2 transition-colors ${
              activeTab === tab.id
                ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                : 'text-white/50 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            type="text"
            placeholder="Search by email or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-[#121212] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
          />
        </div>
        {activeTab === 'applications' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 bg-[#121212] border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="waitlist">Waitlist</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
        </div>
      ) : (
        <div className="bg-[#121212] border border-white/10">
          {activeTab === 'applications' && (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Applicant</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Company</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">MC #</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Date</th>
                  <th className="text-right p-4 text-white/50 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-white/50">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((app) => (
                    <tr key={app._id || app.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <p className="text-white">{app.email}</p>
                        <p className="text-white/50 text-sm">{app.name || app.contact_name}</p>
                      </td>
                      <td className="p-4 text-white/70">{app.company_name || '—'}</td>
                      <td className="p-4 text-white/70 font-mono">{app.mc_number || '—'}</td>
                      <td className="p-4">
                        <StatusBadge status={app.status || 'pending'} />
                      </td>
                      <td className="p-4 text-white/50 text-sm">
                        {app.created_at ? new Date(app.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setSelectedApplication(app)}
                          className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-bold hover:bg-[#F5D47B] transition-colors"
                        >
                          Update Status
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'users' && (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/50 text-sm font-medium">User</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Access Code</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Onboarding</th>
                  <th className="text-right p-4 text-white/50 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-white/50">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user._id || user.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <p className="text-white">{user.email}</p>
                        <p className="text-white/50 text-sm">{user.company_name}</p>
                      </td>
                      <td className="p-4">
                        <span className="text-[#D4AF37] font-mono">{user.access_code || '—'}</span>
                      </td>
                      <td className="p-4">
                        <StatusBadge status={user.application_status || 'pending'} />
                      </td>
                      <td className="p-4">
                        {user.documents_completed ? (
                          <span className="text-green-400 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Complete
                          </span>
                        ) : (
                          <span className="text-yellow-400 flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Incomplete
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setShowActivityFor(user)}
                            className="p-2 hover:bg-white/10 transition-colors"
                            title="View Activity"
                          >
                            <Eye className="w-4 h-4 text-white/60" />
                          </button>
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="px-3 py-1.5 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
                          >
                            Actions
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'pending' && (
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/50 text-sm font-medium">User</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Approved</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Hours Pending</th>
                  <th className="text-left p-4 text-white/50 text-sm font-medium">Has Logged In</th>
                  <th className="text-right p-4 text-white/50 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingOnboarding.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-white/50">
                      <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      All approved users have completed onboarding!
                    </td>
                  </tr>
                ) : (
                  pendingOnboarding.map((user) => (
                    <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <p className="text-white">{user.email}</p>
                        <p className="text-white/50 text-sm">{user.company_name}</p>
                      </td>
                      <td className="p-4 text-white/70">
                        {user.approved_at ? new Date(user.approved_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-4">
                        <span className={`font-mono ${user.needs_reminder ? 'text-red-400' : 'text-white/70'}`}>
                          {user.hours_since_approval || 0}h
                        </span>
                        {user.needs_reminder && (
                          <span className="ml-2 text-xs text-red-400">Needs Reminder</span>
                        )}
                      </td>
                      <td className="p-4">
                        {user.has_logged_in ? (
                          <span className="text-green-400">Yes</span>
                        ) : (
                          <span className="text-white/50">No</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/auth/send-onboarding-reminder?email=${user.email}`);
                              alert('Reminder sent!');
                            } catch (err) {
                              alert('Failed to send reminder');
                            }
                          }}
                          className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-bold hover:bg-[#F5D47B] transition-colors"
                        >
                          Send Reminder
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modals */}
      {selectedApplication && (
        <ApplicationStatusModal
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
          onUpdate={() => fetchData()}
        />
      )}

      {selectedUser && (
        <UserActionsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onAction={() => fetchData()}
        />
      )}

      {showActivityFor && (
        <LoginActivityModal
          user={showActivityFor}
          onClose={() => setShowActivityFor(null)}
        />
      )}
    </div>
  );
};

export default AdminApplicationManagement;
